import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { toast } from "react-toastify";
import { getAuthToken } from "../../utils/auth";
import { handleApiError } from "../handleApiError";
import i18n from "../../i18n";

const API_URL = process.env.REACT_APP_API_URL;

// ========================
// GET HOME POSTS
// ========================
export const getHomePosts = createAsyncThunk(
  "posts/getHome",
  async ({ page = 1, limit = 10, search } = {}, { rejectWithValue }) => {
    try {
      const token = getAuthToken();
      const res = await axios.get(`${API_URL}/post`, {
        params: { page, limit, search },
        headers: { Authorization: `${token}` },
      });

      return {
        data: res.data.data || [],
        responseDetails: res.data.responseDetails || {},
      };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const getTrendingPosts = createAsyncThunk(
  "posts/getTrendingPosts",
  async (
    { page = 1, limit = 10, id, isTrending } = {},
    { rejectWithValue },
  ) => {
    try {
      const token = getAuthToken();
      const res = await axios.get(`${API_URL}/post`, {
        params: { page, limit, id, isTrending },
        headers: { Authorization: `${token}` },
      });

      return {
        data: res.data.data || [],
        responseDetails: res.data.responseDetails || {},
      };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);
// ========================
// GET SINGLE POST
// ========================
export const getPostById = createAsyncThunk(
  "posts/getById",
  async ({ postId }, { rejectWithValue }) => {
    try {
      const token = getAuthToken();
      const res = await axios.get(`${API_URL}/post/${postId}`, {
        headers: { Authorization: `${token}` },
      });
      return res.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

// ========================
// CREATE POST
// ========================
export const createPost = createAsyncThunk(
  "posts/create",
  async (data, { rejectWithValue }) => {
    try {
      const token = getAuthToken();
      const res = await axios.post(`${API_URL}/post`, data, {
        headers: { Authorization: `${token}` },
      });
      // toast.success("Post created successfully");
      return res.data.data;
    } catch (error) {
      handleApiError(error);
      // console.log(error);
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

// ========================
// UPDATE POST
// ========================
export const updatePost = createAsyncThunk(
  "posts/update",
  async ({ postId, data }, { rejectWithValue }) => {
    try {
      const token = getAuthToken();
      const res = await axios.put(`${API_URL}/post/${postId}`, data, {
        headers: { Authorization: `${token}` },
      });
      toast.success(i18n.t("common.postUpdated"));
      return res.data.data;
    } catch (error) {
      handleApiError(error);
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

// ========================
// DELETE POST
// ========================
export const deletePost = createAsyncThunk(
  "posts/delete",
  async (postId, { rejectWithValue }) => {
    try {
      const token = getAuthToken();
      await axios.delete(`${API_URL}/post/${postId}`, {
        headers: { Authorization: `${token}` },
      });
      // toast.success("Post deleted successfully");
      return postId;
    } catch (error) {
      handleApiError(error);
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

// ========================
// GET MY POSTS
// ========================
export const getMyPosts = createAsyncThunk(
  "posts/getMy",
  async ({ page = 1, limit = 10 } = {}, { rejectWithValue }) => {
    try {
      const token = getAuthToken();
      const res = await axios.get(`${API_URL}/post/user/myposts`, {
        params: { page, limit },
        headers: { Authorization: `${token}` },
      });

      return {
        data: res.data.data || [],
        responseDetails: res.data.responseDetails || {},
      };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

// ========================
// ADMIN – GET ALL POSTS
// ========================
export const getAllPostsAdmin = createAsyncThunk(
  "posts/admin/all",
  async (
    { page = 1, limit = 20, search, type, isTrending, isSponsored } = {},
    { rejectWithValue },
  ) => {
    try {
      // console.log(search);
      const token = getAuthToken();
      const params = {
        page,
        limit,
        ...(search && { search }),
        ...(type && { type }),
        ...(isTrending && { isTrending }),
        ...(isSponsored && { isSponsored }),
      };
      // console.log(params);
      const res = await axios.get(`${API_URL}/post/admin/all`, {
        params,
        headers: { Authorization: `${token}` },
      });

      return {
        data: res.data.data || [],
        responseDetails: res.data.responseDetails || {},
      };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

// ========================
// ADMIN – GET PENDING POSTS
// ========================
export const getPendingPosts = createAsyncThunk(
  "posts/admin/pending",
  async ({ page = 1, limit = 20 } = {}, { rejectWithValue }) => {
    try {
      const token = getAuthToken();
      const res = await axios.get(`${API_URL}/post/admin/pending`, {
        params: { page, limit },
        headers: { Authorization: `${token}` },
      });

      return {
        data: res.data.data || [],
        responseDetails: res.data.responseDetails || {},
      };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

// ========================
// ADMIN – TOGGLE ACTIVE
// ========================
export const togglePostActive = createAsyncThunk(
  "posts/admin/toggle",
  async (postId, { rejectWithValue }) => {
    try {
      const token = getAuthToken();
      const res = await axios.patch(
        `${API_URL}/post/admin/toggle/${postId}`,
        {},
        { headers: { Authorization: `${token}` } },
      );
      // toast.success("Post status Active");
      return res.data.data;
    } catch (error) {
      handleApiError(error);
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

// ========================
// ADMIN – TOGGLE TRENDING
// ========================
export const togglePostTrending = createAsyncThunk(
  "posts/adminTrending/toggle",
  async (postId, { rejectWithValue }) => {
    try {
      const token = getAuthToken();
      const res = await axios.patch(
        `${API_URL}/post/admin/trending/${postId}`,
        {},
        { headers: { Authorization: `${token}` } },
      );
      // toast.success("Post status Trending");
      return res.data.data;
    } catch (error) {
      handleApiError(error);
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

// ========================
// ADMIN – TOGGLE SPONSERED
// ========================
export const togglePostSponsored = createAsyncThunk(
  "posts/adminSponsored/toggle",
  async (postId, { rejectWithValue }) => {
    try {
      const token = getAuthToken();
      const res = await axios.patch(
        `${API_URL}/post/admin/sponsored/${postId}`,
        {},
        { headers: { Authorization: `${token}` } },
      );
      // toast.success("Post status Sponsored");
      return res.data.data;
    } catch (error) {
      handleApiError(error);
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);
// ========================
// ADMIN – DELETE POST
// ========================
export const adminDeletePost = createAsyncThunk(
  "posts/admin/delete",
  async (postId, { rejectWithValue }) => {
    try {
      const token = getAuthToken();
      await axios.delete(`${API_URL}/post/admin/${postId}`, {
        headers: { Authorization: `${token}` },
      });
      // toast.success("Post deleted successfully");
      return postId;
    } catch (error) {
      handleApiError(error);
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);
